<template>
    <transition name="modal">
        <div v-if="element && !isLoading" class="modal-mask">
            <div class="modal-wrapper">
                <div class="modal-container">
                    <div class="container">
                        <div class="modal-header">
                            <div class="row">
                                <div class="col">
                                    <h1 v-text="element.$exists ? 'Edit Dealer Zone' : 'Add Dealer Zone'"></h1>
                                </div>
                                <div class="col-auto">
                                    <div aria-label="Close modal window" class="modal-default-button fa fa-window-close d-print-none" @click="$emit('close')"></div>
                                </div>
                            </div>
                        </div>

                        <div class="modal-body">
                            <div v-if="isAdministrator"> <!-- Begin Administrator section -->
                                <div class="row">
                                    <div class="col-md">
                                        <div class="form-group">
                                            <label>Zone Name <span class="required">*</span></label>
                                            <input class="form-control" type="text" v-model="element.name">
                                            <div class="alert alert-danger" v-if="'name' in errors" v-text="errors.name.join()"></div>
                                        </div>
                                    </div>
                                    <div class="col-md">
                                        <companies v-slot="{ companies }">
                                            <div class="form-group">
                                                <label>Company <span class="required">*</span></label>
                                                <select class="form-control" v-model="element.company_id">
                                                    <option v-for="company in companies" :value="company.id" :key="company.id" v-text="company.name"></option>
                                                </select>
                                                <div class="alert alert-danger" v-if="'company_id' in errors" v-text="errors.company_id.join()"></div>
                                            </div>
                                        </companies>
                                    </div>
                                    <div class="col-md">
                                        <div class="form-group">
                                            <label>Contact Email</label>
                                            <input class="form-control" type="text" v-model="element.contact_email">
                                            <div class="alert alert-danger" v-if="'contact_email' in errors" v-text="errors.contact_email.join()"></div>
                                        </div>
                                    </div>
                                </div>

                                <div class="form-group">
                                    <zip-codes v-slot="{ zipCodes }">
                                        <div v-if="zipCodes" class="form-group">
                                            <label>Zip Codes <span class="required">*</span></label>
                                            <v-select multiple v-model="element.zip_codes" :options="zipCodeOptions(zipCodes)"></v-select>
                                            <div class="alert alert-danger" v-if="'zip_codes' in errors" v-text="errors.zip_codes.join()"></div>
                                        </div>
                                    </zip-codes>
                                </div>

                                <div class="row">
                                    <div class="col-md">
                                        <div class="form-group bg-warning p-2">
                                            <label class="text-dark">Price/Gallon <span class="required">*</span></label>
                                            <input class="form-control" type="text" v-model="element.price">
                                            <div class="alert alert-danger" v-if="'price' in errors" v-text="errors.price.join()"></div>
                                        </div>
                                    </div>
                                    <div class="col-md">
                                        <div class="form-group p-2">
                                            <label>Small Order Fee</label>
                                            <input class="form-control" type="text" v-model="element.small_order_fee">
                                            <div class="alert alert-danger" v-if="'small_order_fee' in errors" v-text="errors.small_order_fee.join()"></div>
                                            <div class="help">Under 100 gallons</div>
                                        </div>
                                    </div>
                                    <div class="col-md">
                                        <div class="form-group p-2">
                                            <label>Medium Order Fee</label>
                                            <input class="form-control" type="text" v-model="element.medium_order_fee">
                                            <div class="alert alert-danger" v-if="'medium_order_fee' in errors" v-text="errors.medium_order_fee.join()"></div>
                                            <div class="help">Under 150 gallons</div>
                                        </div>
                                    </div>
                                    <div class="col-md">
                                        <div class="form-group p-2">
                                            <label>Prime Fee</label>
                                            <input class="form-control" type="text" v-model="element.prime_fee">
                                            <div class="alert alert-danger" v-if="'prime_fee' in errors" v-text="errors.prime_fee.join()"></div>
                                        </div>
                                    </div>
                                </div>

                                <div class="row">
                                    <div class="col-md-3">
                                        <div class="form-group">
                                            <label>Processing Fee <span class="required">*</span></label>
                                            <input class="form-control" type="text" v-model="element.processing_fee">
                                            <div class="alert alert-danger" v-if="'processing_fee' in errors" v-text="errors.processing_fee.join()"></div>
                                        </div>
                                    </div>
                                    <div class="col-md-3">
                                        <div class="form-group">
                                            <label>Days Out <span class="required">*</span></label>
                                            <input class="form-control" type="text" v-model="element.days_out">
                                            <div class="alert alert-danger" v-if="'days_out' in errors" v-text="errors.days_out.join()"></div>
                                        </div>
                                    </div>
                                    <div class="col-md-auto">
                                        <div class="form-group">
                                            <label>Emergency Delivery Available <span class="required">*</span></label>
                                            <div>
                                                <div class="form-check-inline">
                                                    <label class="form-check-label">
                                                        <input class="form-check-input" type="radio" value="1" v-model="element.emergency_delivery_available" @click="show_emergency_delivery_fields = true"> Yes
                                                    </label>
                                                </div>
                                                <div class="form-check-inline">
                                                    <label class="form-check-label">
                                                        <input class="form-check-input" type="radio" value="0" v-model="element.emergency_delivery_available" @click="show_emergency_delivery_fields = false"> No
                                                    </label>
                                                </div>
                                            </div>
                                            <div class="alert alert-danger" v-if="'emergency_delivery_available' in errors" v-text="errors.emergency_delivery_available.join()"></div>
                                        </div>
                                    </div>
                                    <div class="col-md">
                                        <div class="form-group">
                                            <label>Emergency Delivery Fee</label>
                                            <div v-if="show_emergency_delivery_fields">
                                                <input class="form-control" type="text" v-model="element.emergency_delivery_fee">
                                                <div class="alert alert-danger" v-if="'emergency_delivery_fee' in errors" v-text="errors.emergency_delivery_fee.join()"></div>
                                            </div>
                                            <div v-else>Not Applicable</div>
                                        </div>
                                    </div>
                                </div>

                                <div class="row" v-if="show_emergency_delivery_fields">
                                    <div class="col">
                                        <div class="form-group">
                                            <label>Emergency Delivery Message</label>
                                            <textarea class="form-control" v-model="element.emergency_delivery_message"></textarea>
                                            <div class="alert alert-danger" v-if="'emergency_delivery_message' in errors" v-text="errors.emergency_delivery_message.join()"></div>
                                        </div>
                                    </div>
                                </div>

                                <div class="row">
                                    <div class="col">
                                        <div class="form-group">
                                            <label>Zone Is Active <span class="required">*</span></label>
                                            <div>
                                                <div class="form-check-inline">
                                                    <label class="form-check-label">
                                                        <input class="form-check-input" type="radio" :value="1" v-model="element.active"> Yes
                                                    </label>
                                                </div>
                                                <div class="form-check-inline">
                                                    <label class="form-check-label">
                                                        <input class="form-check-input" type="radio" :value="0" v-model="element.active"> No
                                                    </label>
                                                </div>
                                            </div>
                                            <div class="alert alert-danger" v-if="'active' in errors" v-text="errors.active.join()"></div>
                                        </div>
                                    </div>
                                </div>
                            </div> <!-- End Administrator section -->

                            <div v-else>  <!-- Begin Dealer section -->
                                <div class="row">
                                    <div class="col-md">
                                        <div class="form-group">
                                            <label>Zone Name</label>
                                            <div>{{ element.name }}</div>
                                        </div>
                                    </div>
                                    <div class="col-md">
                                        <div class="form-group">
                                            <label>Company</label>
                                            <div>{{ element.company.name }}</div>
                                        </div>
                                    </div>
                                    <div class="col-md">
                                        <div class="form-group">
                                            <label>Contact Email</label>
                                            <div>{{ element.contact_email }}</div>
                                        </div>
                                    </div>
                                </div>

                                <div class="row">
                                    <div class="col-md">
                                        <div class="form-group">
                                            <label>Zip Codes</label>
                                            <div v-text="element.zip_codes.map(zip => `${zip.city} (${zip.zip_code})`).join(', ')"></div>
                                        </div>
                                    </div>
                                </div>

                                <div class="row">
                                    <div class="col-md">
                                        <div class="form-group">
                                            <label>Prime Fee</label>
                                            <div>{{ usd(element.prime_fee) }}</div>
                                            <div class="alert alert-danger" v-if="'prime_fee' in errors" v-text="errors.prime_fee.join()"></div>
                                        </div>
                                    </div>
                                    <div class="col-md">
                                        <div class="form-group">
                                            <label>Small Order Fee</label>
                                            <div>{{ usd(element.small_order_fee) }}</div>
                                            <div class="help">Under 100 gallons</div>
                                        </div>
                                    </div>
                                    <div class="col-md">
                                        <div class="form-group">
                                            <label>Medium Order Fee</label>
                                            <div>{{ usd(element.medium_order_fee) }}</div>
                                            <div class="help">Under 150 gallons</div>
                                        </div>
                                    </div>
                                    <div class="col-md">
                                        <div class="form-group">
                                            <label>Processing Fee</label>
                                            <div>{{ usd(element.processing_fee) }}</div>
                                        </div>
                                    </div>
                                </div>

                                <div class="row">
                                    <div class="col-md">
                                        <div class="form-group">
                                            <label>Price/Gallon</label>
                                            <div v-if="element.company && element.company.can_set_price">
                                                <input class="form-control" type="text" v-model="element.price">
                                                <div class="alert alert-danger" v-if="'price' in errors" v-text="errors.price.join()"></div>
                                            </div>
                                            <div v-else>{{ usd3(element.price) }}</div>
                                        </div>
                                    </div>
                                    <div class="col-md">
                                        <div class="form-group">
                                            <label>Days Out</label>
                                            <div v-if="element.company && element.company.can_set_delivery_days">
                                                <input class="form-control" type="text" v-model="element.days_out">
                                                <div class="alert alert-danger" v-if="'days_out' in errors" v-text="errors.days_out.join()"></div>
                                            </div>
                                            <div v-else>{{ element.days_out }}</div>
                                        </div>
                                    </div>
                                    <div class="col">
                                        <div class="form-group">
                                            <label>Emergency Delivery Fee</label>
                                            <div v-if="show_emergency_delivery_fields">
                                                <div v-if="element.company && element.company.can_set_emergency_delivery_price">
                                                    <input class="form-control" type="text" v-model="element.emergency_delivery_fee">
                                                    <div class="alert alert-danger" v-if="'emergency_delivery_fee' in errors" v-text="errors.emergency_delivery_fee.join()"></div>
                                                </div>
                                                <div v-else>{{ usd(element.emergency_delivery_fee) }}</div>
                                            </div>
                                            <div v-else>Not Available</div>
                                        </div>
                                    </div>
                                </div>

                                <div class="row" v-if="show_emergency_delivery_fields">
                                    <div class="col">
                                        <div class="form-group">
                                            <label>Emergency Delivery Message</label>
                                            <div>{{ element.emergency_delivery_message }}</div>
                                        </div>
                                    </div>
                                </div>
                            </div> <!-- End Dealer section -->


                            <div class="d-flex justify-content-between d-print-none mt-3">
                                <div>
                                    <delete-button v-if="isAdministrator" @confirmed="destroyAndClose" title="Delete Dealer Zone">
                                        <p>
                                            Are you sure you want to delete the dealer zone {{ element.name }}?
                                        </p>
                                    </delete-button>
                                </div>

                                <div class="ml-auto">
                                    <button aria-label="Close Dealer Zone Modal window" class="btn btn-medium" @click="$emit('close')">Cancel</button>

                                    <button class="btn btn-primary" @click="saveAndClose" v-text="element.$exists ? 'Update' : 'Save'"></button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </transition>
</template>

<script>
import vSelect from "vue-select";
import numeral from "numeral";
import deleteButton from './DeleteButton';
import { ContinuousCollectionComponent, ElementComponent } from 'clockvine-vue';
import ElementOperations from './helpers/ElementOperations';


export default {
    props: ['id'],

    components: {
        vSelect, 
        deleteButton,
        companies: ContinuousCollectionComponent.for('companies'),
        zipCodes: ContinuousCollectionComponent.for('zipCodes')
    },

    mixins: [
        ElementComponent.for('dealerZones').with({
            computed: {
                newElement: () => ({
                    name: '',
                    active: 1,
                    days_out: 3,
                    small_order_fee: 0,
                    medium_order_fee: 0,
                    prime_fee: 0,
                    emergency_delivery_fee: 0,
                    emergency_delivery_available: 0,
                })
            }
        }),
        ElementOperations
    ],

    data: () => ({
        isAdministrator: false,
        errors: {},
        show_emergency_delivery_fields: false
    }),

    mounted() {
        var user = JSON.parse(this.$root.$el.attributes['data-user'].value);

        this.isAdministrator = (user.role == 'Administrator');

        if (this.element) {
            this.show_emergency_delivery_fields = this.element.emergency_delivery_available;
        }

    },
    methods: {

        booleanFormat(value) {
            return (value) ? 'Yes' : 'No';
        },

        percentFormat(amount) {
            return numeral(amount).format('0.00') + '%';
        },

        zipCodeOptions(zipCodes) {
            return zipCodes.map(zip => {
                zip.value = zip.zip_code;
                zip.label = `${zip.zip_code} (${zip.city} ${zip.state.name})`;
                return zip;
            });
        },
        
        saveAndClose() {
            this.save().then(() => {
                this.$emit('close');
            });
        },

        destroyAndClose() {
            this.delete().then(() => {
                this.$emit('close');
            });
        },

    },
}
</script>
